// =============================================================================
// ui/shell/ProActionLimit.tsx — the dialog a Pro action gets instead of running.
// -----------------------------------------------------------------------------
// The action-level twin of `FreeTierLimit`, and deliberately NOT that component
// reused: its body sentence is "Nothing you already have is affected — everything
// stays, and stays editable", which is the right thing to say about a COUNT cap
// and a strange thing to say about a button that did nothing.
//
// ── ⚠ THE BUTTON STAYS LIVE, IT IS NOT DISABLED ──
//
// Same argument as the free-tier caps, and the old client makes the same call:
// its refusal fires from the action, not from a greyed-out control. A disabled
// button with no explanation is a discoverability failure, not a guard — the
// reader cannot tell "I may not" from "it is broken", and a paywall the reader
// never meets never sells anything.
//
// ── ⚠ WHAT THIS IS NOT ──
//
// The old client answers a refused Coach Me with a full-screen branded teaser
// that shows a REAL sample read of the user's own leaks, blurs the rest and puts
// a price on it (app/index.html:18893). That is a marketing surface with its own
// content pipeline, and it is NOT ported here. This dialog is the honest
// refusal with a route to the plan; the teaser is tracked separately.
//
// Saying so matters because the difference is the whole conversion: beta's
// version sells, and a refusal that merely refuses does not.
//
// ⚠ NO NEW CSS — `.modal-scrim` / `.modal` / `.modal-head` / `.modal-body` /
// `.modal-foot` are the house classes, and `scripts/check-styles.mjs` fails the
// build for a class with no rule.
// =============================================================================

export interface ProActionLimitProps {
  /** What the user reached for, e.g. "The AI Coach". */
  title: string;
  /** The refusal sentence, from `useProAction`. */
  message: string;
  /** One line saying what paying gets them. */
  pitch: string;
  onClose: () => void;
  /** Send them to the plan. Same destination as the old client's `showUpgrade`. */
  onSeePlans?: (() => void) | undefined;
}

export function ProActionLimit(
  { title, message, pitch, onClose, onSeePlans }: ProActionLimitProps,
) {
  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal" data-testid="pro-action-limit">
        <header className="modal-head">
          <b>{title}</b>
          {/* ⚠ Always closeable — a paywall that traps the reader is a bug
              report, not a conversion. The old client's free-tier suite spends
              assertions on exactly this. */}
          <button type="button" className="btn" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="modal-body" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 34, lineHeight: 1 }} aria-hidden="true">🔒</div>
          <p style={{ margin: '10px 0 0', fontWeight: 600 }}>{message}</p>
          <p className="muted" style={{ fontSize: 13, lineHeight: 1.55, marginTop: 6 }}>
            {pitch}
          </p>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn" onClick={onClose}>Close</button>
          {onSeePlans ? (
            <button type="button" className="btn primary" onClick={onSeePlans}>See plans</button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
